import { Center, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { isUuid } from '@shared';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { FlowTemplateForm, toFlowTemplateInput, toFlowTemplateValues } from './-components/flow-template-form';
import { FlowTemplateSubscription, UpdateFlowTemplateMutation } from './-components/graphql';

export const Route = createFileRoute('/_authed/system/flow-templates/$id')({
  component: SystemFlowTemplatePage,
});

function SystemFlowTemplatePage() {
  const { id } = Route.useParams();
  const hasValidId = isUuid(id);
  const [{ data, error, fetching }] = useSubscription({
    query: FlowTemplateSubscription,
    variables: { id },
    pause: !hasValidId,
  });
  const [, runUpdate] = useMutation(UpdateFlowTemplateMutation);

  if (hasValidId && fetching && !data && !error) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const template = data?.flowTemplatesByPk ?? null;

  if (!template) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message={error ? getErrorMessage(error, 'Failed to load flow template') : 'Template not found'} />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Edit Flow Template</Title>
          <Text c="dimmed" mt="xs">
            {template.name}
          </Text>
        </div>
        <FlowTemplateForm
          initialValues={toFlowTemplateValues(template)}
          submitLabel="Save"
          onSubmit={async (values) => {
            const result = await runUpdate({ id: template.id, set: toFlowTemplateInput(values) });

            return result.error ? getErrorMessage(result.error, 'Failed to save flow template') : null;
          }}
        />
      </Stack>
    </Container>
  );
}
